/* eslint-disable react/no-unescaped-entities */
import { Formik, FormikHelpers, FormikProps, FormikValues } from 'formik'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import { Music2, ArrowRight, CheckCircle2, Shield, Mic2 } from 'lucide-react'
import Layout from 'components/common/layout/Layout'
import { Input } from 'components/common/field/input'
import { useAuthContext } from 'hooks/AuthContext'
import { Label } from 'components/common/label'
import { BASE_URL, NEXT_PUBLIC_GOOGLE_AUTH_ENABLE } from 'env'
import Logo from 'components/common/logo/Logo'
import GoogleAuth from '../../components/login-page/GoogleAuth'
import LoginForm from '../../components/login-page/LoginForm'

export default function Index() {
  const { push } = useRouter()
  const { actions: contextActions, state } = useAuthContext()
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [knownUser, setKnownUser] = useState(false)
  const [passAuth, setPassAuth] = useState(false)
  const [unsettingToken, setUnsettingToken] = useState(false)
  const [newUser, setNewUser] = useState(false)
  const [referralId, setReferralId] = useState<string | null>(null)

  const formRef = useRef<FormikProps<FormikValues>>(null)
  const [email, setEmail] = useState('')
  const [globalError, setGlobalError] = useState('')

  useEffect(() => {
    setEmail(new URLSearchParams(window.location.search).get('email') as string)
    setReferralId(
      new URLSearchParams(window.location.search).get('refferal-id'),
    )
  }, [])

  useEffect(() => {
    if (formRef.current && email) {
      formRef.current.handleSubmit()
    }
  }, [email])

  if (state.key) {
    if (newUser) {
      router.push('/login/role')
    } else if (state.userType === 'teacher') {
      push('/user')
    } else if (state.userType === 'student') {
      push('/student/teacher')
    } else if (state.userType === 'none') {
      router.push('/login/role')
    }
  }

  const handleSubmit = async (values: FormikValues, actions: FormikHelpers<FormikValues>) => {
    setLoading(true)
    setGlobalError('')
    const searchParams = new URLSearchParams(window.location.search)
    const utmParams = {}

    Array.from(searchParams.entries()).forEach(([key, value]) => {
      if (key.startsWith('utm_')) {
        utmParams[key] = value
      }
    })
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)
    if (!emailValid) {
      actions.setFieldError(
        'email',
        'Please enter a valid email address.',
      )
      setLoading(false)
      return
    }

    if (!knownUser) {
      const tempNewUser = {
        email: values?.email,
        utm_queries: utmParams || null,
        referral_id: referralId || null,
      }
      await fetch(`${BASE_URL}/api/v1/auth/email/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tempNewUser),
      }).then(async (res) => {
        if (res.ok) {
          const body = await res.json()
          const key = body?.token
          if (key) {
            contextActions.setKey(key)
            setNewUser(true)
            router.push('/login/role')
          } else {
            setKnownUser(true)
            const authTypePass = body?.auth_type === 'password'
            setPassAuth(authTypePass)
          }
        }
      })
    } else if (passAuth) {
      const tempNewUser = {
        email: values?.email,
        password: values?.password,
      }
      actions.setFieldTouched('password', true)
      await fetch(`${BASE_URL}/api/v1/auth/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tempNewUser),
      }).then(async (res) => {
        const body = await res.json()
        if (res.ok) {
          const key = body?.key
          if (key) {
            contextActions.setKey(key)
            router.push('/user')
          }
        } else {
          Object.keys(body).forEach((key) => {
            const value = body[key]
            if (Array.isArray(value)) {
              actions.setFieldError(key, value[0])
            } else {
              actions.setFieldError(key, value)
            }
          })
        }
      })
    } else if (unsettingToken) {
      const tempNewUser = {
        email: values?.email,
        token: values?.token,
      }
      await fetch(`${BASE_URL}/api/v1/auth/unset/token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tempNewUser),
      }).then(async (res) => {
        const body = await res.json()
        if (res.ok) {
          const key = body?.key
          if (key) {
            contextActions.setKey(key)
            router.push('/user')
          }
        } else {
          Object.keys(body).forEach((key) => {
            const value = body[key]
            if (Array.isArray(value)) {
              actions.setFieldError(key, value[0])
            } else {
              actions.setFieldError(key, value)
            }
          })
        }
      })
    } else {
      const tempNewUser = {
        email: values?.email,
        token: values?.token,
        referral_id: referralId || null,
      }
      await fetch(`${BASE_URL}/api/v1/auth/token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tempNewUser),
      }).then(async (res) => {
        const body = await res.json()
        if (res.ok) {
          const key = body?.token
          if (key) {
            contextActions.setKey(key)
            router.push('/user')
          }
        } else {
          Object.keys(body).forEach((key) => {
            const value = body[key]
            if (Array.isArray(value)) {
              actions.setFieldError(key, value[0])
            } else {
              actions.setFieldError(key, value)
            }
          })
        }
      })
    }

    setLoading(false)
  }

  useEffect(() => {
    if (passAuth) {
      const passwordInput = document.getElementById('password-input')
      if (passwordInput) {
        passwordInput.focus()
      }
    }
  }, [passAuth])

  const formTitle = knownUser
    ? passAuth
      ? 'Enter your password'
      : 'Check your inbox'
    : 'Login or sign up'

  const formDescription = knownUser
    ? passAuth
      ? 'Use the password linked to this account so you can get back into your teaching workspace quickly.'
      : 'We sent a temporary code so you can get into your workspace without extra setup.'
    : 'Start with your email and we will route you into the right teacher or student flow.'

  return (
    <Layout meta={{ robots: 'index, archive, follow' }} showNavbar={false}>
      <Formik
        innerRef={formRef}
        initialValues={
                    { email } as {
                        non_field_errors?: null
                        email?: string
                    }
        }
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {(props) => {
          const tempGlobalError = globalError || props?.errors?.non_field_errors?.toString()
          return (
            <form
              className="mx-auto flex w-full max-w-6xl flex-1 items-center justify-center py-4 md:py-8"
              onSubmit={props.handleSubmit}
            >
              <div className="grid w-full gap-6 lg:grid-cols-[1.08fr_0.92fr]">
                <section className="relative overflow-hidden rounded-[32px] border border-[#f4d7df] bg-gradient-to-br from-[#fff7f9] via-white to-[#fff0f4] p-6 shadow-[0_24px_70px_rgba(33,30,87,0.08)] sm:p-8 lg:p-10">
                  <div className="absolute -right-20 top-0 h-48 w-48 rounded-full bg-[#fd4367]/10 blur-3xl" />
                  <div className="absolute left-0 top-32 h-32 w-32 rounded-full bg-[#211e57]/6 blur-3xl" />

                  <div className="relative">
                    <Logo />
                    <div className="mt-8 inline-flex rounded-full bg-[#fd4367] px-4 py-1 text-sm font-medium text-white">
                      MeetLala sign-in
                    </div>
                    <h1 className="mt-5 max-w-xl text-4xl font-semibold tracking-tight text-[#211e57] sm:text-5xl">
                      Start in a music-teaching workspace, not a generic meeting app.
                    </h1>
                    <p className="mt-4 max-w-xl text-base leading-7 text-[#5b6175]">
                      Enter your email first. From there, we guide teachers into their classroom workflow and students into the lesson-side experience.
                    </p>

                    <div className="mt-8 grid gap-3 sm:grid-cols-3">
                      {[
                        {
                          title: 'Stable classroom link',
                          description: 'Teachers keep one room for repeat lessons.',
                          icon: Music2,
                        },
                        {
                          title: 'Audio-first setup',
                          description: 'Built around music lessons, not generic calls.',
                          icon: Mic2,
                        },
                        {
                          title: 'Trusted access',
                          description: 'Use password or email-code sign-in depending your setup.',
                          icon: Shield,
                        },
                      ].map((item) => (
                        <div key={item.title} className="rounded-[24px] border border-white/80 bg-white/85 p-4 shadow-sm">
                          <div className="inline-flex rounded-2xl bg-[#fff4f6] p-2.5 text-[#fd4367]">
                            <item.icon className="h-4 w-4" />
                          </div>
                          <p className="mt-4 text-sm font-semibold text-[#211e57]">{item.title}</p>
                          <p className="mt-2 text-sm leading-6 text-[#5b6175]">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                <section className="rounded-[32px] border border-[#ebe7f5] bg-white p-6 shadow-[0_24px_70px_rgba(33,30,87,0.08)] sm:p-8">
                  <div className="mb-6">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#fd4367]">Access your workspace</p>
                    <h2 className="mt-2 text-3xl font-semibold tracking-tight text-[#211e57]">{formTitle}</h2>
                    <p className="mt-3 text-sm leading-6 text-[#5b6175]">{formDescription}</p>
                  </div>

                  {NEXT_PUBLIC_GOOGLE_AUTH_ENABLE && (
                    <div className="mb-6 rounded-[24px] border border-[#ece8f6] bg-[#fcfcff] p-4">
                      <GoogleAuth />
                    </div>
                  )}

                  <div className="space-y-4">
                    <Label.Box>
                      <Label>email</Label>
                      <Input
                        name="email"
                        placeholder="Enter your email"
                        type="text"
                      />
                    </Label.Box>

                    <LoginForm
                      props={props}
                      passAuth={passAuth}
                      setLoading={setLoading}
                      setPassAuth={setPassAuth}
                      setGlobalError={setGlobalError}
                      setKnownUser={setKnownUser}
                      setUnsettingToken={setUnsettingToken}
                      knownUser={knownUser}
                      tempGlobalError={tempGlobalError}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#fd4367] px-6 py-4 text-base font-semibold text-white transition-colors hover:bg-[#ea3f62] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    <span>{loading ? 'Working...' : unsettingToken ? 'Unset password' : 'Continue'}</span>
                    {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                  </button>

                  <div className="mt-6 rounded-[24px] border border-[#f4d7df] bg-[#fff8fb] p-4">
                    <div className="flex items-start gap-3">
                      <div className="rounded-full bg-white p-2 text-[#fd4367]">
                        <CheckCircle2 className="h-4 w-4" />
                      </div>
                      <p className="text-sm leading-6 text-[#5b6175]">
                        New teachers continue into role selection and classroom setup before any subscription prompt appears.
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </form>
          )
        }}
      </Formik>
      {/* <JivoScript /> */}
    </Layout>
  )
}
